import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X, LogOut } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { categories } from "../mock";

const announcements = [
  "Envio GRÁTIS em compras acima de 35€",
  "3 amostras grátis em cada encomenda",
  "Garantia de 2 anos em todos os dispositivos",
  "30 dias para devolução gratuita",
];

export default function Header() {
  const { totalItems, setIsOpen } = useCart();
  const { user, login, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/loja${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-background">
      {/* Announcement marquee */}
      <div className="bg-[hsl(20,14%,12%)] text-white overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-2 text-xs tracking-wide">
          {[...announcements, ...announcements].map((a, i) => (
            <span key={i} className="mx-8 flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-[hsl(12,62%,58%)]" />
              {a}
            </span>
          ))}
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center gap-4">
          <button
            className="md:hidden p-1"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          <Link to="/" className="shrink-0">
            <span className="font-serif text-2xl md:text-3xl font-semibold tracking-[0.2em] text-foreground">
              RADIX
            </span>
          </Link>

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex flex-1 max-w-xl mx-auto items-center bg-secondary rounded-full px-4 h-11"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Procurar dispositivos, marcas..."
              className="flex-1 bg-transparent outline-none px-3 text-sm placeholder:text-muted-foreground"
            />
          </form>

          <div className="ml-auto flex items-center gap-1 md:gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="hidden md:flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-secondary transition-colors outline-none">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <span className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="text-sm max-w-[120px] truncate">{user.name || user.email}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" /> Terminar sessão
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={login}
                className="hidden md:flex items-center gap-2 p-2 hover:text-primary transition-colors text-sm"
              >
                <User className="h-5 w-5" />
                <span>Entrar</span>
              </button>
            )}
            <button className="hidden md:flex flex-col items-center p-2 hover:text-primary transition-colors">
              <Heart className="h-5 w-5" />
            </button>
            <button
              onClick={() => setIsOpen(true)}
              className="relative p-2 hover:text-primary transition-colors"
              aria-label="Abrir carrinho"
            >
              <ShoppingBag className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-semibold h-4.5 min-w-4.5 px-1 rounded-full flex items-center justify-center h-4 min-w-4">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Category nav */}
        <nav className="hidden md:block border-t border-border">
          <div className="max-w-7xl mx-auto px-6">
            <ul className="flex items-center gap-8 h-12 text-sm">
              <li>
                <Link to="/loja" className="font-medium hover:text-primary transition-colors">
                  Todos os produtos
                </Link>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/loja/${c.slug}`}
                    className="hover:text-primary transition-colors text-muted-foreground hover:text-foreground"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/loja" className="text-primary font-semibold">
                  Saldos
                </Link>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-4/5 max-w-xs bg-background p-5 fade-up">
            <div className="flex items-center justify-between mb-6">
              <span className="font-serif text-2xl tracking-[0.2em]">RADIX</span>
              <button onClick={() => setMobileOpen(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>
            <form
              onSubmit={handleSearch}
              className="flex items-center bg-secondary rounded-full px-4 h-11 mb-6"
            >
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Procurar..."
                className="flex-1 bg-transparent outline-none px-3 text-sm"
              />
            </form>
            <ul className="space-y-1">
              <li>
                <Link
                  to="/loja"
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 font-medium border-b border-border"
                >
                  Todos os produtos
                </Link>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/loja/${c.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 border-b border-border text-muted-foreground"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-6 border-t border-border">
              {user ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="text-sm truncate">{user.name || user.email}</span>
                  </div>
                  <button
                    onClick={() => { logout(); setMobileOpen(false); }}
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <LogOut className="h-4 w-4" /> Sair
                  </button>
                </div>
              ) : (
                <button
                  onClick={login}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-full h-11 font-medium"
                >
                  <User className="h-5 w-5" /> Entrar com Google
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
